/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import type { EditorThemeClasses } from 'lexical';

import './LightEditorTheme.css';

const theme: EditorThemeClasses = {
   blockCursor: 'LightEditorTheme__blockCursor',
   characterLimit: 'LightEditorTheme__characterLimit',
   code: 'LightEditorTheme__code',
   codeHighlight: {
      atrule: 'LightEditorTheme__tokenAttr',
      attr: 'LightEditorTheme__tokenAttr',
      boolean: 'LightEditorTheme__tokenProperty',
      builtin: 'LightEditorTheme__tokenSelector',
      cdata: 'LightEditorTheme__tokenComment',
      char: 'LightEditorTheme__tokenSelector',
      class: 'LightEditorTheme__tokenFunction',
      'class-name': 'LightEditorTheme__tokenFunction',
      comment: 'LightEditorTheme__tokenComment',
      constant: 'LightEditorTheme__tokenProperty',
      deleted: 'LightEditorTheme__tokenProperty',
      doctype: 'LightEditorTheme__tokenComment',
      entity: 'LightEditorTheme__tokenOperator',
      function: 'LightEditorTheme__tokenFunction',
      important: 'LightEditorTheme__tokenVariable',
      inserted: 'LightEditorTheme__tokenSelector',
      keyword: 'LightEditorTheme__tokenAttr',
      namespace: 'LightEditorTheme__tokenVariable',
      number: 'LightEditorTheme__tokenProperty',
      operator: 'LightEditorTheme__tokenOperator',
      prolog: 'LightEditorTheme__tokenComment',
      property: 'LightEditorTheme__tokenProperty',
      punctuation: 'LightEditorTheme__tokenPunctuation',
      regex: 'LightEditorTheme__tokenVariable',
      selector: 'LightEditorTheme__tokenSelector',
      string: 'LightEditorTheme__tokenSelector',
      symbol: 'LightEditorTheme__tokenProperty',
      tag: 'LightEditorTheme__tokenProperty',
      url: 'LightEditorTheme__tokenOperator',
      variable: 'LightEditorTheme__tokenVariable',
   },
   embedBlock: {
      base: 'LightEditorTheme__embedBlock',
      focus: 'LightEditorTheme__embedBlockFocus',
   },
   hashtag: 'LightEditorTheme__hashtag',
   heading: {
      h1: 'LightEditorTheme__h1',
      h2: 'LightEditorTheme__h2',
      h3: 'LightEditorTheme__h3',
      h4: 'LightEditorTheme__h4',
      h5: 'LightEditorTheme__h5',
      h6: 'LightEditorTheme__h6',
   },
   image: 'editor-image',
   link: 'LightEditorTheme__link',
   list: {
      listitem: 'LightEditorTheme__listItem',
      listitemChecked: 'LightEditorTheme__listItemChecked',
      listitemUnchecked: 'LightEditorTheme__listItemUnchecked',
      nested: {
         listitem: 'LightEditorTheme__nestedListItem',
      },
      olDepth: [
         'LightEditorTheme__ol1',
         'LightEditorTheme__ol2',
         'LightEditorTheme__ol3',
         'LightEditorTheme__ol4',
         'LightEditorTheme__ol5',
      ],
      ul: 'LightEditorTheme__ul',
   },
   ltr: 'LightEditorTheme__ltr',
   mark: 'LightEditorTheme__mark',
   markOverlap: 'LightEditorTheme__markOverlap',
   paragraph: 'LightEditorTheme__paragraph',
   quote: 'LightEditorTheme__quote',
   rtl: 'LightEditorTheme__rtl',
   table: 'LightEditorTheme__table',
   tableAddColumns: 'LightEditorTheme__tableAddColumns',
   tableAddRows: 'LightEditorTheme__tableAddRows',
   tableCell: 'LightEditorTheme__tableCell',
   tableCellActionButton: 'LightEditorTheme__tableCellActionButton',
   tableCellActionButtonContainer:
      'LightEditorTheme__tableCellActionButtonContainer',
   tableCellEditing: 'LightEditorTheme__tableCellEditing',
   tableCellHeader: 'LightEditorTheme__tableCellHeader',
   tableCellPrimarySelected: 'LightEditorTheme__tableCellPrimarySelected',
   tableCellResizer: 'LightEditorTheme__tableCellResizer',
   tableCellSelected: 'LightEditorTheme__tableCellSelected',
   tableCellSortedIndicator: 'LightEditorTheme__tableCellSortedIndicator',
   tableResizeRuler: 'LightEditorTheme__tableCellResizeRuler',
   tableSelected: 'LightEditorTheme__tableSelected',
   text: {
      bold: 'LightEditorTheme__textBold',
      code: 'LightEditorTheme__textCode',
      italic: 'LightEditorTheme__textItalic',
      strikethrough: 'LightEditorTheme__textStrikethrough',
      subscript: 'LightEditorTheme__textSubscript',
      superscript: 'LightEditorTheme__textSuperscript',
      underline: 'LightEditorTheme__textUnderline',
      underlineStrikethrough: 'LightEditorTheme__textUnderlineStrikethrough',
   },
};

export default theme;
